// Utility functions for smart reminder notifications

export const calculateReminderTime = (itemDateTime, reminderTiming) => {
  const itemDate = new Date(itemDateTime);
  const reminderDate = new Date(itemDate);

  switch (reminderTiming) {
    case '5min':
      reminderDate?.setMinutes(reminderDate?.getMinutes() - 5);
      break;
    case '15min':
      reminderDate?.setMinutes(reminderDate?.getMinutes() - 15);
      break;
    case '30min':
      reminderDate?.setMinutes(reminderDate?.getMinutes() - 30);
      break;
    case '1hour':
      reminderDate?.setHours(reminderDate?.getHours() - 1);
      break;
    case '2hours':
      reminderDate?.setHours(reminderDate?.getHours() - 2);
      break;
    case '1day':
      reminderDate?.setDate(reminderDate?.getDate() - 1);
      break;
    case '2days':
      reminderDate?.setDate(reminderDate?.getDate() - 2);
      break;
    case '1week':
      reminderDate?.setDate(reminderDate?.getDate() - 7);
      break;
    default:
      reminderDate?.setMinutes(reminderDate?.getMinutes() - 15);
  }

  return reminderDate;
};

export const getUpcomingReminders = (items, memberSettings, currentDateTime = new Date()) => {
  const reminders = [];
  const now = currentDateTime;
  const futureWindow = new Date(now);
  futureWindow?.setHours(futureWindow?.getHours() + 24); // Look ahead 24 hours

  items?.forEach(item => {
    const memberId = item?.personId || item?.assignedTo;
    const memberSetting = memberSettings?.find(s => s?.memberId === memberId);

    if (!memberSetting?.enabled) return;

    // Get item date/time
    let itemDateTime;
    if (item?.date && item?.time) {
      // Calendar event format
      itemDateTime = new Date(`${item?.date}T${item?.time}`);
    } else if (item?.date) {
      // Long-term task format
      itemDateTime = new Date(item?.date);
    } else if (item?.time !== undefined) {
      // Daily task format (time is hour 0-23)
      const today = new Date();
      itemDateTime = new Date(today?.getFullYear(), today?.getMonth(), today?.getDate(), item?.time, 0);
    } else {
      return;
    }

    // Calculate reminder times based on frequency
    const frequencies = memberSetting?.frequency || ['15min'];
    frequencies?.forEach(freq => {
      const reminderTime = calculateReminderTime(itemDateTime, freq);

      if (reminderTime >= now && reminderTime <= futureWindow) {
        reminders?.push({
          id: `${item?.id}-${freq}`,
          itemId: item?.id,
          itemTitle: item?.title,
          itemType: item?.category || item?.priority || 'task',
          memberId,
          memberName: item?.personName || item?.assignedTo,
          reminderTime,
          itemTime: itemDateTime,
          frequency: freq,
          isRecurring: item?.isRecurring || false
        });
      }
    });
  });

  return reminders?.sort((a, b) => a?.reminderTime - b?.reminderTime);
};

export const formatReminderMessage = (reminder) => {
  const timeUntil = reminder?.itemTime - reminder?.reminderTime;
  const minutes = Math?.floor(timeUntil / (1000 * 60));
  const hours = Math?.floor(minutes / 60);
  const days = Math?.floor(hours / 24);

  let timeText;
  if (days > 0) {
    timeText = `in ${days} day${days > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    timeText = `in ${hours} hour${hours > 1 ? 's' : ''}`;
  } else {
    timeText = `in ${minutes} minute${minutes > 1 ? 's' : ''}`;
  }

  return `${reminder?.itemTitle} ${timeText}`;
};

export const shouldShowReminder = (reminder, currentDateTime = new Date()) => {
  const now = currentDateTime;
  const reminderTime = new Date(reminder?.reminderTime);
  const timeDiff = reminderTime - now;

  // Show reminder if it's within 5 minutes of reminder time
  return timeDiff <= 5 * 60 * 1000 && timeDiff >= -5 * 60 * 1000;
};

export const getDefaultMemberNotificationSettings = (memberId) => {
  return {
    memberId,
    enabled: true,
    frequency: ['15min', '1day'],
    deliveryTiming: 'smart', // 'smart', 'exact', 'morning', 'evening'
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00',
    notifyForRecurring: true
  };
};

export const isInQuietHours = (dateTime, quietHoursStart, quietHoursEnd) => {
  const time = dateTime?.toTimeString()?.substring(0, 5);
  
  if (quietHoursStart < quietHoursEnd) {
    return time >= quietHoursStart && time <= quietHoursEnd;
  } else {
    // Quiet hours span midnight
    return time >= quietHoursStart || time <= quietHoursEnd;
  }
};

export const adjustForDeliveryTiming = (reminderTime, deliveryTiming, quietHoursStart, quietHoursEnd) => {
  const adjusted = new Date(reminderTime);

  if (deliveryTiming === 'morning') {
    adjusted?.setHours(8, 0, 0, 0);
  } else if (deliveryTiming === 'evening') {
    adjusted?.setHours(18, 0, 0, 0);
  } else if (deliveryTiming === 'smart') {
    if (isInQuietHours(adjusted, quietHoursStart, quietHoursEnd)) {
      // Move to end of quiet hours
      const [endHour, endMin] = quietHoursEnd?.split(':')?.map(Number);
      adjusted?.setHours(endHour, endMin, 0, 0);
    }
  }

  return adjusted;
};